/**
 * Created by Ning Jiang on 2021-06-22.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {gsap} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {BigWinView} from "./BigWinView";
import {Serial} from "../../../Serial";
import {SerialGameConfig} from "../../../SerialGameConfig";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {SerialSound} from "../../../sound/SerialSound";
import {SerialGameMode} from "../../../server/SerialGameMode";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {Layers, SerialLayers} from "../../../view/SerialLayers";

export class BigWinController extends WinPresentationController {

    private _view:BigWinView;
    private _levelTimeline:TimelineLite | null;

    constructor() {
        super("BigWinController", true);
        this._view = new BigWinView();
    }

    protected hasWin():boolean {
        const data = Serial.model.parsedGameData;
        return data.currentWinRatioConfig!.bigWinLevel > -1 && !data.nextMode.includes("RESPIN");
    }

    protected async createWinAnimation():Promise<any> {
        await new Promise<any>(resolve => {
            SerialSound.fadeMusic(0, 300);
            this.playBigWinLevel(0, resolve);
        });
    }

    private playBigWinLevel(level:number, resolve:(value:any) => void):void {
        const data = Serial.model.parsedGameData;
        if(level > data.currentWinRatioConfig!.bigWinLevel) {
            this._levelTimeline = null;
            this.playEnding(data.currentWinRatioConfig!.bigWinLevel, resolve);
        } else {
            const isEndLevel = level === data.currentWinRatioConfig!.bigWinLevel;
            const currentRatio = SerialGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === level)!;
            const from:number = level === 0 ? 0 : data.playedBetValue * currentRatio.ratio;
            const to:number = Math.min(data.playedBetValue * 187000, isEndLevel ? data.totalSpinWinnings : data.playedBetValue * SerialGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === (level + 1))!.ratio);
            const tooSmallEndLevel:boolean = isEndLevel && this.isLevelWinTooSmall(level, from, to);
            const tl = gsap.timeline();
            tl.add(this.getLevelAnimation(level, currentRatio.duration, from, to, isEndLevel));
            tl.add(() => this.playBigWinLevel(level + 1, resolve));
            this._levelTimeline = tl;
            if(tooSmallEndLevel) {
                this._levelTimeline.progress(1);
            } else {
                this._flowStateController.setSkipCallback(() => this.skip!());
                SerialSound.soundPlayer.playTrack({name : SerialSound.BIG_WIN[level]});
                SerialSound.soundPlayer.playTrack({name : SerialSound.BIG_WIN_LEVEL_CHANGE});
            }
        }
    }

    private isLevelWinTooSmall(level:number, from:number, to:number):boolean {
        if(level === 0) {
            return false;
        }

        return (to - from) <= (from * 0.1);
    }

    private getLevelAnimation(level:number, duration:number, from:number, to:number, isEndLevel:boolean):TimelineLite {
        const tl = gsap.timeline();
        tl.add([
            <gsap.core.Timeline><unknown>Serial.background.screenTransition.getAnimationAutoShowHide(),
            () => {
                SerialLayers.getLayer(Layers.BIG_WIN)?.addChild(Serial.background.screenTransition);
                Serial.bigWinCountUp.scale.set(1.2);
            },
            Serial.bigWinCountUp.getCountUpAnimation(from, to, duration, SerialLayers.getLayer(Layers.WIN_COUNT_UP)!, 100),
            gsap.to(Serial.bigWinCountUp.scale, {duration : duration, x : 1, y : 1, ease : `power4.out`}),
            this._view.getLevelAnimation(level, duration, isEndLevel)
        ]);
        return tl
    }

    protected skip() {
        for(let i = 0; i < 5; i++) {
            SerialSound.soundPlayer.stopTrack(SerialSound.BIG_WIN[i]);
        }
        if(this._levelTimeline && this._levelTimeline.isActive()) {
            this._levelTimeline.progress(1);
        }
    }

    private playEnding(endLevel:number, resolve:(value:any) => void):void {
        this._flowStateController.setSkipCallback();

        if(!Serial.model.parsedGameData.brokeTheBank) {
            SerialSound.soundPlayer.playTrack({name : SerialSound.BIG_WIN_END});
        }

        if(!this.isNextScreen()) {
            SerialSound.fadeMusic(1, 400);
        }
        Serial.bigWinCountUp.stay();
        Serial.waysWon.stay();
        SlotGame.keypadPlugin.setWin(Serial.model.parsedGameData.totalWin, Serial.model.parsedGameData.showTotalWin, BelowStakeWinRestriction.isWinBelowStake());
        this._view.playEnding(endLevel);
        resolve(undefined);
    }

    private isNextScreen():boolean {
        const data = Serial.model.parsedGameData;
        if(data.nextMode === SerialGameMode.CAPWIN_GAME) {
            return true;
        }

        // fs/super fs intro
        if(!data.mode.includes(GameMode.FREESPIN) && data.nextMode.includes(GameMode.FREESPIN)) {
            return true;
        }

        // fs upgrade
        if(data.mode.includes(GameMode.FREESPIN) && !data.mode.includes("SUPER") && data.nextMode === SerialGameMode.FREESPIN_SUPER) {
            return true;
        }

        if(data.mode.includes(GameMode.FREESPIN) && data.nextMode === GameMode.NORMAL) {
            return true;
        }

        return false;
    }

}